import { BRAND_DEFAULT, BRAND_NORTHSTAR } from '@platform/design-system';
/** The mock accepts exactly this authenticator code. */
export const MOCK_TOTP_CODE = '123456';
export const MOCK_TOTP_SECRET = 'JBSWY3DPEHPK3PXPJBSWY3DPEHPK3PXP';
/** The temporary password an invited mock person arrives with. */
export const MOCK_TEMP_PASSWORD = 'Welcome-Aboard-2026!';
/** The code the mock "emails" to confirm a self sign-up. */
export const MOCK_EMAIL_CODE = '123456';
const T0 = '2026-08-02T14:00:00Z';
/** Display fields identity returns on every membership (`GET /me/memberships`). */
const TENANT_DISPLAY = {
    northstar: { tenantName: 'Northstar Benefits', tier: 'pool' },
    harbor: { tenantName: 'Harbor Life Group', tier: 'silo' },
    lighthouse: { tenantName: 'Lighthouse Advisors', tier: 'pool' },
};
function snapshot(pkg, version, snapshotAt) {
    return {
        packageId: pkg.packageId,
        packageVersion: version,
        features: [...pkg.features],
        stripePriceId: pkg.stripePriceId,
        ...(pkg.assistant ? { assistant: structuredClone(pkg.assistant) } : {}),
        snapshotAt,
    };
}
export function createMockWorld() {
    let counter = 100;
    const nextId = (prefix) => `${prefix}${counter++}`;
    const packages = [
        {
            packageId: 'pkg_solo',
            product: 'sprocket',
            name: 'Solo',
            tiers: ['pool'],
            features: ['crm'],
            stripePriceId: 'price_solo001',
            billingModel: 'flat',
            version: 1,
            retired: false,
            createdAt: '2026-07-01T09:00:00Z',
            updatedAt: '2026-07-01T09:00:00Z',
        },
        {
            packageId: 'pkg_team',
            product: 'sprocket',
            name: 'Team',
            tiers: ['pool', 'silo'],
            features: ['crm', 'forms', 'campaigns'],
            stripePriceId: 'price_team001',
            billingModel: 'perSeat',
            version: 2,
            retired: false,
            createdAt: '2026-07-01T09:00:00Z',
            updatedAt: '2026-09-10T16:00:00Z',
        },
        {
            packageId: 'pkg_enterprise',
            product: 'sprocket',
            name: 'Enterprise',
            tiers: ['silo'],
            features: ['crm', 'forms', 'campaigns', 'assistant', 'sms'],
            stripePriceId: 'price_ent001',
            billingModel: 'perSeat',
            assistant: {
                models: ['fast', 'standard'],
                monthlyLimitMicroUsd: 200_000_000,
                dailyLimitMicroUsd: 20_000_000,
                memberMonthlyLimitMicroUsd: 25_000_000,
            },
            version: 1,
            retired: false,
            createdAt: '2026-07-01T09:00:00Z',
            updatedAt: '2026-07-01T09:00:00Z',
        },
    ];
    const team = packages[1];
    const enterprise = packages[2];
    const tenants = [
        {
            tenantId: 'northstar',
            slug: 'northstar',
            product: 'sprocket',
            name: 'Northstar Benefits',
            tier: 'pool',
            status: 'active',
            cellId: 'shared-1',
            adminEmail: 'avery@northstar.example',
            // Adopted at v1; the catalog has since moved to v2 — the snapshot stays put.
            entitlement: snapshot({ ...team, features: ['crm', 'forms'] }, 1, T0),
            flags: {},
            createdAt: T0,
            updatedAt: T0,
            brand: BRAND_NORTHSTAR,
            billing: {
                status: 'active',
                packageId: 'pkg_team',
                seats: { licensed: 10, used: 4 },
                billingModel: 'perSeat',
                currentPeriodEnd: '2026-10-01T00:00:00Z',
                cancelAtPeriodEnd: false,
                nextInvoice: { amountDueCents: 29_900, currency: 'usd', dueAt: '2026-10-01T00:00:00Z' },
                lastInvoice: {
                    invoiceId: 'in_mock0001',
                    status: 'paid',
                    amountDueCents: 29_900,
                    amountPaidCents: 29_900,
                    currency: 'usd',
                    paidAt: '2026-09-01T00:00:00Z',
                    attemptCount: 1,
                },
                portalAvailable: true,
            },
        },
        {
            tenantId: 'harbor',
            slug: 'harbor',
            product: 'sprocket',
            name: 'Harbor Life Group',
            tier: 'silo',
            status: 'active',
            cellId: 'harbor-1',
            adminEmail: 'casey@harbor.example',
            entitlement: snapshot(enterprise, 1, '2026-07-20T10:00:00Z'),
            flags: { assistant: true },
            createdAt: '2026-07-20T10:00:00Z',
            updatedAt: '2026-09-15T10:00:00Z',
            brand: BRAND_DEFAULT,
            billing: {
                status: 'past_due',
                packageId: 'pkg_enterprise',
                seats: { licensed: 50, used: 2 },
                billingModel: 'perSeat',
                currentPeriodEnd: '2026-09-15T00:00:00Z',
                cancelAtPeriodEnd: false,
                nextInvoice: { amountDueCents: 249_900, currency: 'usd', dueAt: '2026-09-15T00:00:00Z' },
                lastInvoice: {
                    invoiceId: 'in_mock0002',
                    status: 'open',
                    amountDueCents: 249_900,
                    amountPaidCents: 0,
                    currency: 'usd',
                    attemptCount: 2,
                },
                portalAvailable: true,
            },
        },
        {
            tenantId: 'lighthouse',
            slug: 'lighthouse',
            product: 'sprocket',
            name: 'Lighthouse Advisors',
            tier: 'pool',
            status: 'locked',
            cellId: 'shared-1',
            adminEmail: 'dana@lighthouse.example',
            entitlement: snapshot(packages[0], 1, '2026-09-19T08:00:00Z'),
            flags: {},
            createdAt: '2026-09-19T08:00:00Z',
            updatedAt: '2026-09-19T08:00:00Z',
            billing: {
                status: 'none',
                packageId: 'pkg_solo',
                seats: { licensed: 1, used: 1 },
                billingModel: 'flat',
                cancelAtPeriodEnd: false,
                portalAvailable: false,
            },
        },
    ];
    const people = [
        person('mock|avery', 'avery@northstar.example', 'Avery Quinn', 'Northstar-Owner-2026!'),
        person('mock|jordan', 'jordan@northstar.example', 'Jordan Lee', 'Northstar-Agent-2026!'),
        person('mock|riley-n', 'riley@northstar.example', 'Riley Chen', 'Northstar-Admin-2026!'),
        {
            ...person('mock|morgan', 'morgan@northstar.example', 'Morgan Diaz', MOCK_TEMP_PASSWORD),
            invited: true,
        },
        person('mock|casey', 'casey@harbor.example', 'Casey Morgan', 'Harbor-Manager-2026!'),
        {
            ...person('mock|dana', 'dana@lighthouse.example', 'Dana Park', MOCK_TEMP_PASSWORD),
            invited: true,
        },
        {
            ...person('mock|sam', 'sam@sprocket.example', 'Sam Rivera', 'Platform-Admin-2026!'),
            staff: true,
            platformAdmin: true,
            totpEnrolled: true,
        },
        {
            ...person('mock|riley-s', 'riley@sprocket.example', 'Riley Chen', 'Platform-Support-2026!'),
            staff: true,
        },
    ];
    const memberships = [
        m('northstar', 'mock|avery', ['TenantAdmin'], T0, 'active'),
        m('northstar', 'mock|jordan', ['Agent'], '2026-08-05T09:30:00Z', 'active', {
            officeCode: 'NY',
            agentCode: 'JL01',
        }),
        m('northstar', 'mock|riley-n', ['TenantAdmin'], '2026-08-11T16:15:00Z', 'active'),
        m('northstar', 'mock|morgan', ['Agent'], '2026-09-18T11:00:00Z', 'invited', {
            officeCode: 'NY',
            agentCode: 'MD02',
        }),
        m('harbor', 'mock|casey', ['Manager'], '2026-07-20T10:00:00Z', 'active', {
            officeCode: 'BOS',
            grants: ['office#BOS'],
        }),
        m('harbor', 'mock|avery', ['Agent'], '2026-07-22T10:00:00Z', 'active', {
            officeCode: 'BOS',
            agentCode: 'AQ07',
        }),
        m('lighthouse', 'mock|dana', ['TenantAdmin'], '2026-09-19T08:00:00Z', 'invited'),
    ];
    const invitations = [
        {
            invitationId: '01J8M0RQ4XK9Z3T7VN6YB2CDEF',
            tenantId: 'northstar',
            email: 'morgan@northstar.example',
            roles: ['Agent'],
            officeCode: 'NY',
            agentCode: 'MD02',
            status: 'pending',
            invitedBy: 'mock|avery',
            expiresAt: '2026-09-21T11:00:00Z',
            createdAt: '2026-09-18T11:00:00Z',
        },
    ];
    const subscriptions = tenants.map((t) => ({
        tenantId: t.tenantId,
        status: t.billing.status,
        ...(t.billing.packageId ? { packageId: t.billing.packageId } : {}),
        ...(t.entitlement ? { stripePriceId: t.entitlement.stripePriceId } : {}),
        billingModel: t.billing.billingModel,
        seats: { ...t.billing.seats },
        ...(t.billing.currentPeriodEnd ? { currentPeriodEnd: t.billing.currentPeriodEnd } : {}),
        cancelAtPeriodEnd: t.billing.cancelAtPeriodEnd,
        mode: 'sandbox',
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
    }));
    return {
        people,
        tenants,
        memberships,
        invitations,
        packages,
        subscriptions,
        signups: new Map(),
        selfSignUp: false,
        personalProduct: null,
        nextId,
    };
}
/**
 * The mock id of a personal tenant: `p-` + 24 base32 characters, derived from the product and
 * the person as the control plane derives it (there from SHA-256; here from a small FNV hash —
 * a fixture, not a contract). A repeat sign-up resolves to the same tenant.
 */
export function mockPersonalTenantId(product, sub) {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz234567';
    let out = '';
    let h = 0x811c9dc5;
    for (let round = 0; out.length < 24; round++) {
        for (const ch of `${product}:${sub}:${round}`)
            h = Math.imul(h ^ ch.charCodeAt(0), 0x01000193);
        for (let i = 0; i < 6 && out.length < 24; i++)
            out += alphabet[(h >>> (i * 5)) & 31];
    }
    return `p-${out}`;
}
/**
 * The mock world of a PERSONAL product (MyMedMetrics, `tenantShape: personal`): one flat
 * monthly package, a pool that takes self sign-up, and three people — one with an active plan
 * (a tenant of one, TenantAdmin of it), one signed up with no plan yet, one who never entered
 * the emailed code. Fictional names, `.example` domains.
 */
export function createPersonalMockWorld(product = 'mymedmetrics') {
    let counter = 100;
    const nextId = (prefix) => `${prefix}${counter++}`;
    const monthly = {
        packageId: 'pkg_mmm-monthly',
        product,
        name: 'Monthly',
        tiers: ['pool'],
        features: ['metrics'],
        stripePriceId: 'price_mmmmonthly01',
        billingModel: 'flat',
        version: 1,
        retired: false,
        createdAt: '2026-11-01T09:00:00Z',
        updatedAt: '2026-11-01T09:00:00Z',
    };
    const jamie = {
        ...person('mock|jamie', 'jamie@mymedmetrics.example', 'Jamie Rivera', 'Jamie-Rivera-2026!'),
        blurb: 'Monthly plan, active',
    };
    const alex = {
        ...person('mock|alex', 'alex@mymedmetrics.example', 'Alex Kim', 'Alex-Kim-2026!!'),
        blurb: 'Account created, no plan yet',
    };
    const taylor = {
        ...person('mock|taylor', 'taylor@mymedmetrics.example', 'Taylor Brooks', 'Taylor-Brooks-2026!'),
        unconfirmed: true,
        blurb: `Email not confirmed yet (code ${MOCK_EMAIL_CODE})`,
    };
    const jamieTenant = mockPersonalTenantId(product, jamie.sub);
    const T1 = '2026-11-02T15:00:00Z';
    const tenant = {
        tenantId: jamieTenant,
        slug: jamieTenant,
        product,
        name: jamie.name,
        tier: 'pool',
        status: 'active',
        cellId: `${product}-1`,
        adminEmail: jamie.email,
        entitlement: snapshot(monthly, 1, T1),
        flags: {},
        createdAt: T1,
        updatedAt: T1,
        billing: {
            status: 'active',
            packageId: monthly.packageId,
            seats: { licensed: 1, used: 1 },
            billingModel: 'flat',
            currentPeriodStart: T1,
            currentPeriodEnd: '2026-12-02T15:00:00Z',
            cancelAtPeriodEnd: false,
            portalAvailable: true,
        },
    };
    return {
        people: [jamie, alex, taylor],
        tenants: [tenant],
        memberships: [
            {
                tenantId: jamieTenant,
                tenantName: jamie.name,
                tier: 'pool',
                sub: jamie.sub,
                roles: ['TenantAdmin'],
                grants: [],
                createdAt: T1,
                status: 'active',
            },
        ],
        invitations: [],
        packages: [monthly],
        subscriptions: [
            {
                tenantId: jamieTenant,
                status: 'active',
                packageId: monthly.packageId,
                stripePriceId: monthly.stripePriceId,
                billingModel: 'flat',
                seats: { licensed: 1, used: 1 },
                currentPeriodEnd: '2026-12-02T15:00:00Z',
                cancelAtPeriodEnd: false,
                mode: 'sandbox',
                createdAt: T1,
                updatedAt: T1,
            },
        ],
        signups: new Map([
            [jamieTenant, { slug: jamieTenant, status: 'active', startedAt: 0, ownerSub: jamie.sub }],
        ]),
        selfSignUp: true,
        personalProduct: product,
        nextId,
    };
}
function person(sub, email, name, password) {
    return {
        sub,
        email,
        name,
        password,
        invited: false,
        totpEnrolled: false,
        staff: false,
        platformAdmin: false,
    };
}
function m(tenantId, sub, roles, createdAt, status, extra = {}) {
    const tenant = TENANT_DISPLAY[tenantId] ?? { tenantName: tenantId, tier: 'pool' };
    return {
        tenantId,
        tenantName: tenant.tenantName,
        tier: tenant.tier,
        sub,
        roles,
        ...(extra.officeCode ? { officeCode: extra.officeCode } : {}),
        ...(extra.agentCode ? { agentCode: extra.agentCode } : {}),
        grants: extra.grants ?? [],
        createdAt,
        status,
    };
}
export function tenantOf(world, tenantId) {
    return world.tenants.find((t) => t.tenantId === tenantId);
}
export function personOf(world, sub) {
    return world.people.find((p) => p.sub === sub);
}
export function personByEmail(world, email) {
    const needle = email.trim().toLowerCase();
    return world.people.find((p) => p.email.toLowerCase() === needle);
}
export function membershipsOf(world, sub) {
    return world.memberships.filter((mm) => mm.sub === sub);
}
export function tierOf(world, tenantId) {
    return tenantOf(world, tenantId)?.tier ?? 'pool';
}
const MOCK_TERMS_URL = 'https://example.test/legal/terms';
const MOCK_PRIVACY_URL = 'https://example.test/legal/privacy';
/**
 * Named situations the apps' end-to-end tests ask for with `?mock=<name>` on the first page load
 * (mock mode only; the world resets on a reload):
 *
 * - `documents-due`: the product has new versions of its terms and privacy policy, and every
 *   person in the world accepted the old ones.
 * - `notices`: every tenant has two notices from staff, one that can be dismissed and one that
 *   stays until the person says they have read it.
 */
export function applyMockScenario(world, name) {
    if (name === 'documents-due') {
        const products = new Set(world.tenants.map((t) => t.product));
        world.legalDocuments = { ...(world.legalDocuments ?? {}) };
        for (const product of products)
            world.legalDocuments[product] = [
                { document: 'terms', version: '2026-11', url: MOCK_TERMS_URL },
                { document: 'privacy', version: '2026-11', url: MOCK_PRIVACY_URL },
            ];
        world.consents = Object.fromEntries(world.people.map((p) => [p.sub, { documents: { terms: '2026-01', privacy: '2026-01' } }]));
    }
    if (name === 'notices') {
        world.notices ??= [];
        for (const t of world.tenants) {
            world.notices.push({
                noticeId: `n_info${t.tenantId
                    .replace(/[^a-z0-9]/gi, '')
                    .slice(0, 12)
                    .padEnd(12, '0')}`,
                tenantId: t.tenantId,
                severity: 'important',
                title: 'Planned maintenance on Saturday',
                body: 'The app will be unavailable for about ten minutes on Saturday morning.',
                publishedAt: '2026-11-03T09:00:00Z',
                acks: {},
            }, {
                noticeId: `n_sec0${t.tenantId
                    .replace(/[^a-z0-9]/gi, '')
                    .slice(0, 12)
                    .padEnd(12, '0')}`,
                tenantId: t.tenantId,
                severity: 'security',
                title: 'Important information about your account',
                body: 'We are writing to tell you about an incident that involved some account details. Please read what happened and what you can do.',
                link: { label: 'What happened', url: 'https://example.test/notices/incident' },
                publishedAt: '2026-11-03T10:00:00Z',
                acks: {},
            });
        }
    }
}
//# sourceMappingURL=world.js.map